import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Icon,
  Table,
  Button,
  Loader,
  Message,
  Modal,
  Form,
  Input,
  Dimmer,
  Pagination,
  Dropdown,
  Label,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { clientService, getCnpjSearchUserMessage } from '../../services/clients/ClientService';
import { ClientResponse, ClientListResponse, CreateClientRequest, ClientContractsSummaryResponse, ClientListFiltersRequest } from '../../types/dtos';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import CompanyDataViewer from '../../components/CompanyDataViewer';
import { mapClientToCompanyData } from '../../components/CompanyDataViewer/utils';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { CredentialsModal } from '../../components/Suspensions/credentials-modal';
import { contractService, ContractLineDto, ContractResponseDto } from '../../services/contracts/ContractService';
import ClientSummaryAccordionRow from '../../components/clients/ClientSummaryAccordionRow';
import ClientContractHistoryModal from '../../components/clients/ClientContractHistoryModal';
import {
  CLIENT_CLASSIFICATION_OPTIONS,
  countClientInternalNoteEntries,
  getClientClassificationMeta,
  normalizeClientClassificationCode,
  readClientClassification,
  readClientInternalNotes,
} from '../../utils/clientClassification';

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<ClientListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientResponse | null>(null);
  const [formData, setFormData] = useState<Partial<CreateClientRequest>>({});
  const [searchingCnpj, setSearchingCnpj] = useState(false);
  const cnpjSearchAbortRef = useRef<AbortController | null>(null);
  const [formFieldsVisible, setFormFieldsVisible] = useState(false);

  // Estados para o modal de visualização dos dados da ReceitaWS
  const [receitaWsModalOpen, setReceitaWsModalOpen] = useState(false);
  const [receitaWsData, setReceitaWsData] = useState<ClientResponse | null>(null);
  const [viewClientModalOpen, setViewClientModalOpen] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [selectedLineForCredentials, setSelectedLineForCredentials] = useState<string | null>(null);
  const [credentialsClientIdForModal, setCredentialsClientIdForModal] = useState<string | null>(null);
  const [loadingClientCredentialsId, setLoadingClientCredentialsId] = useState<string | null>(null);
  const [clientSummaries, setClientSummaries] = useState<Record<string, ClientContractsSummaryResponse>>({});
  const [loadingSummaryIds, setLoadingSummaryIds] = useState<Set<string>>(new Set());
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    situacaoCadastral: '',
    cidade: '',
    estado: '',
    clientClassification: '',
    temContrato: 'all',
    statusMeuTim: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1
  });
  
  // Estados para validação de WhatsApp
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappFile, setWhatsappFile] = useState<File | null>(null);
  const [whatsappEmail, setWhatsappEmail] = useState('');
  const [validatingWhatsapp, setValidatingWhatsapp] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyClient, setHistoryClient] = useState<ClientListResponse | null>(null);
  const [historyScrollTarget, setHistoryScrollTarget] = useState<'observacoes' | undefined>();
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [enqueueingMeuTimHealthCheck, setEnqueueingMeuTimHealthCheck] = useState(false);
  
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  // Verificar se o usuário tem permissão de Admin (pode editar/criar/deletar)
  const isAdmin = user?.roles && user.roles.includes('Admin');
  const canOpenClientHistory = isAdmin;
  
  // Verificar se o usuário pode visualizar (Admin, Manager, Seller, TeamLeader)
  const canView = user?.roles && (user.roles.includes('Admin') || user.roles.includes('Manager') || user.roles.includes('Seller') || user.roles.includes('TeamLeader'));

  const handleOpenClientHistory = (
    client: ClientListResponse,
    options?: { scrollTo?: 'observacoes' }
  ) => {
    setHistoryClient(client);
    setHistoryScrollTarget(options?.scrollTo);
    setHistoryModalOpen(true);
  };

  const handleHistoryMetadataUpdated = useCallback(
    (payload: { clientId: string; clientClassification: string; internalNotes: string }) => {
      const mergeClientMetadata = (client: ClientListResponse): ClientListResponse => {
        if (client.id !== payload.clientId) return client;
        return {
          ...client,
          clientClassification: payload.clientClassification,
          internalNotes: payload.internalNotes,
        };
      };

      setClients((prev) => prev.map(mergeClientMetadata));
      setHistoryClient((prev) => (prev ? mergeClientMetadata(prev) : prev));
    },
    []
  );

  useEffect(() => {
    loadClients({ pageNumber: 1 });
  }, []);

  const buildServerFilters = (
    pageNumber?: number,
    pageSize?: number,
    customFilters?: typeof filters
  ): ClientListFiltersRequest => {
    const selectedFilters = customFilters ?? filters;
    const serverFilters: ClientListFiltersRequest = {
      search: selectedFilters.search || undefined,
      situacaoCadastral: selectedFilters.situacaoCadastral || undefined,
      cidade: selectedFilters.cidade || undefined,
      estado: selectedFilters.estado || undefined,
      pageNumber: pageNumber ?? pagination.currentPage,
      pageSize: pageSize ?? pagination.pageSize
    };

    if (selectedFilters.temContrato === 'sim') {
      serverFilters.hasContract = true;
    } else if (selectedFilters.temContrato === 'nao') {
      serverFilters.hasContract = false;
    }

    if (selectedFilters.clientClassification) {
      serverFilters.clientClassification = selectedFilters.clientClassification;
    }

    if (isAdmin && selectedFilters.statusMeuTim) {
      serverFilters.meuTimStatus = selectedFilters.statusMeuTim as 'configurado' | 'nao-tem-acesso' | 'sem-linhas';
    }

    return serverFilters;
  };

  const loadClients = async (options?: { pageNumber?: number; pageSize?: number; customFilters?: typeof filters }) => {
    try {
      setLoading(true);
      setError(null);
      const serverFilters = buildServerFilters(options?.pageNumber, options?.pageSize, options?.customFilters);
      const pagedData = await clientService.getClientsPaged(serverFilters);
      setClients(pagedData.items);
      setExpandedClients(new Set());
      setPagination({
        currentPage: pagedData.currentPage,
        pageSize: pagedData.pageSize,
        totalCount: pagedData.totalCount,
        totalPages: pagedData.totalPages
      });
      await loadClientSummaries(pagedData.items.map((client) => client.id));
    } catch (err) {
      setError('Erro ao carregar clientes');
      console.error('Erro ao carregar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    await loadClients({ pageNumber: 1 });
  };

  const handleClearFilters = async () => {
    const clearedFilters = {
      search: '',
      situacaoCadastral: '',
      cidade: '',
      estado: '',
      clientClassification: '',
      temContrato: 'all',
      statusMeuTim: ''
    };
    setFilters(clearedFilters);
    await loadClients({ pageNumber: 1, pageSize: pagination.pageSize, customFilters: clearedFilters });
  };

  const loadClientSummaries = async (clientIds: string[]) => {
    if (!clientIds.length) {
      setClientSummaries({});
      return;
    }

    setLoadingSummaryIds(new Set(clientIds));
    try {
      const summaries = await clientService.getClientsContractsSummary(clientIds);
      const summaryMap = summaries.reduce<Record<string, ClientContractsSummaryResponse>>((acc, summary) => {
        acc[summary.clientId] = summary;
        return acc;
      }, {});
      setClientSummaries(summaryMap);
    } catch (err) {
      console.error('Erro ao carregar resumo de contratos dos clientes:', err);
      showError('Não foi possível carregar o resumo de contratos dos clientes.');
    } finally {
      setLoadingSummaryIds(new Set());
    }
  };

  const toggleClientExpansion = (clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const clientTableColSpan = isAdmin ? 12 : 10;

  const allVisibleSelected = useMemo(
    () => clients.length > 0 && clients.every((client) => selectedClientIds.has(client.id)),
    [clients, selectedClientIds]
  );

  const selectedCount = selectedClientIds.size;

  const hasClientFiltersApplied =
    filters.search.trim() !== '' ||
    filters.situacaoCadastral !== '' ||
    filters.cidade.trim() !== '' ||
    filters.estado.trim() !== '' ||
    filters.clientClassification !== '' ||
    filters.temContrato !== 'all' ||
    (isAdmin && filters.statusMeuTim !== '');

  const pageSizeOptions = [10, 20, 30, 50].map((n) => ({
    key: n,
    text: String(n),
    value: n,
  }));

  const getMeuTimStatus = (summary?: ClientContractsSummaryResponse) => {
    if (!summary) {
      return { label: 'Carregando...', color: '#7f8c8d' };
    }

    if (summary.hasMeuTimCredentials) {
      return { label: 'Tem acesso', color: '#21ba45' };
    }

    if (!summary.hasContract) {
      return { label: 'Sem contrato', color: '#7f8c8d' };
    }

    if (summary.suspensionLines === 0) {
      return { label: 'Sem linhas de suspensão', color: '#7f8c8d' };
    }

    return { label: 'Não tem acesso', color: '#f39c12' };
  };

  const handleCreateClient = () => {
    setSelectedClient(null);
    setFormData({
      cnpj: '',
      clientClassification: 'CLIENTE_REGULAR',
    });
    setFormFieldsVisible(false);
    setModalOpen(true);
  };

  const handleEditClient = async (client: ClientListResponse) => {
    try {
      // Buscar cliente completo para edição
      const fullClient = await clientService.getClientById(client.id);
      setSelectedClient(fullClient);
      setFormData({
        tipoCliente: fullClient.tipoCliente,
        segmento: fullClient.segmento,
        cnpj: fullClient.cnpj,
        razaoSocial: fullClient.razaoSocial,
        nomeFantasia: fullClient.nomeFantasia,
        clientClassification: normalizeClientClassificationCode(readClientClassification(fullClient)),
        telefone1: fullClient.telefone1,
        email: fullClient.email,
        cep: fullClient.cep,
        estado: fullClient.estado,
        cidade: fullClient.cidade,
        logradouro: fullClient.logradouro,
        numero: fullClient.numero,
        bairro: fullClient.bairro,
        complemento: fullClient.complemento,
        pontoReferencia: fullClient.pontoReferencia,
        situacaoCadastral: fullClient.situacaoCadastral
      });
      setFormFieldsVisible(true); // Mostra todos os campos ao editar
      setModalOpen(true);
    } catch (err) {
      setError('Erro ao carregar dados do cliente');
      console.error('Erro ao carregar cliente:', err);
    }
  };

  const handleViewClient = async (clientId: string) => {
    try {
      const client = await clientService.getClientById(clientId);
      setSelectedClient(client);
      setViewClientModalOpen(true);
    } catch (err) {
      setError('Erro ao carregar cliente');
      console.error('Erro ao carregar cliente:', err);
    }
  };

  const handleViewMeuTimCredentials = async (client: ClientListResponse) => {
    try {
      if (!isAdmin) {
        showError('Apenas administradores podem visualizar o acesso Meu TIM.');
        return;
      }

      setLoadingClientCredentialsId(client.id);
      setError(null);
      setCredentialsClientIdForModal(null);
      setSelectedLineForCredentials(null);

      const contractsResponse = await contractService.getContractsWithFilters({
        clientId: client.id,
        pageNumber: 1,
        pageSize: 100
      });

      const allLines = contractsResponse.items
        .flatMap((contract: ContractResponseDto) => contract.linhas || [])
        .filter((line: ContractLineDto) => !line.isDeleted);

      if (allLines.length > 0) {
        const preferredLine =
          allLines.find((line: ContractLineDto) => line.requerSuspensao && !!line.id) ||
          allLines.find((line: ContractLineDto) => !!line.id);

        if (!preferredLine?.id) {
          showError('Não foi possível identificar uma linha válida para configurar o Meu TIM.');
          return;
        }

        setSelectedLineForCredentials(preferredLine.id);
      } else {
        setCredentialsClientIdForModal(client.id);
      }

      setCredentialsModalOpen(true);
    } catch (err) {
      console.error('Erro ao carregar credenciais Meu TIM do cliente:', err);
      showError('Erro ao carregar acesso Meu TIM do cliente.');
    } finally {
      setLoadingClientCredentialsId(null);
    }
  };

  const abortCnpjSearch = useCallback(() => {
    cnpjSearchAbortRef.current?.abort();
    cnpjSearchAbortRef.current = null;
    setSearchingCnpj(false);
  }, []);

  useEffect(() => () => {
    cnpjSearchAbortRef.current?.abort();
  }, []);

  const handleSearchCnpj = async () => {
    if (!formData.cnpj || formData.cnpj.trim() === '') {
      setError('Digite um CNPJ válido');
      return;
    }

    cnpjSearchAbortRef.current?.abort();
    const ac = new AbortController();
    cnpjSearchAbortRef.current = ac;

    try {
      setSearchingCnpj(true);
      setError(null);

      const response = await clientService.searchAndSaveClient(formData.cnpj, {
        signal: ac.signal,
      });

      if (ac.signal.aborted) return;

      if (response.success && response.data) {
        setModalOpen(false);
        setReceitaWsData(response.data);
        setReceitaWsModalOpen(true);
      } else {
        setError(response.message || 'Erro ao buscar dados do CNPJ');
      }
    } catch (err) {
      if (ac.signal.aborted) return;
      const message = getCnpjSearchUserMessage(err);
      if (message) setError(message);
      if (message) console.error('Erro ao buscar CNPJ:', err);
    } finally {
      if (cnpjSearchAbortRef.current === ac) {
        cnpjSearchAbortRef.current = null;
        setSearchingCnpj(false);
      }
    }
  };

  const handleConfirmCnpjData = async () => {
    if (!receitaWsData) return;

    try {
      // Cliente já foi criado na busca, apenas fecha o modal e recarrega lista
      setReceitaWsModalOpen(false);
      setReceitaWsData(null);
      await loadClients();
      setError(null);
    } catch (err) {
      setError('Erro ao confirmar dados do cliente');
      console.error('Erro ao confirmar dados:', err);
    }
  };

  const handleSaveClient = async () => {
    try {
      const payload = {
        ...formData,
        clientClassification: normalizeClientClassificationCode(
          typeof formData.clientClassification === 'string'
            ? formData.clientClassification
            : undefined
        ),
      };

      if (selectedClient) {
        // Update
        await clientService.updateClient(selectedClient.id, payload);
      } else {
        // Create
        await clientService.createClient(payload as CreateClientRequest);
      }
      setModalOpen(false);
      await loadClients();
    } catch (err) {
      setError('Erro ao salvar cliente');
      console.error('Erro ao salvar cliente:', err);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await clientService.deleteClient(clientId);
        await loadClients();
      } catch (err) {
        setError('Erro ao excluir cliente');
        console.error('Erro ao excluir cliente:', err);
      }
    }
  };

  const handleActivateClient = async (clientId: string) => {
    try {
      await clientService.activateClient(clientId);
      setError(null);
      await loadClients();
    } catch (err) {
      setError('Erro ao ativar cliente');
      console.error('Erro ao ativar cliente:', err);
    }
  };

  const handleDeactivateClient = async (clientId: string) => {
    try {
      await clientService.deactivateClient(clientId);
      setError(null);
      await loadClients();
    } catch (err) {
      setError('Erro ao desativar cliente');
      console.error('Erro ao desativar cliente:', err);
    }
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj) return '';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const handleOpenWhatsappModal = () => {
    setWhatsappFile(null);
    setWhatsappEmail(user?.email || '');
    setWhatsappModalOpen(true);
  };

  const toggleClientSelection = (clientId: string, checked: boolean) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clientId);
      else next.delete(clientId);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      for (const client of clients) {
        if (checked) next.add(client.id);
        else next.delete(client.id);
      }
      return next;
    });
  };

  const clearSelectedClients = () => setSelectedClientIds(new Set());

  const handleEnqueueMeuTimHealthCheck = async () => {
    if (!isAdmin) {
      showError('Apenas administradores podem verificar acessos Meu TIM.');
      return;
    }

    const selectedIds = Array.from(selectedClientIds);
    if (selectedIds.length === 0) {
      showError('Selecione ao menos um cliente.');
      return;
    }

    try {
      setEnqueueingMeuTimHealthCheck(true);
      const result = await clientService.enqueueMeuTimHealthCheck(selectedIds);
      showSuccess(
        `Health-check Meu TIM: ${result.enqueued} enfileirado(s), ${result.skipped} ignorado(s).`
      );
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Erro ao enfileirar health-check Meu TIM.';
      showError(message);
      console.error('Erro ao enfileirar health-check Meu TIM:', err);
    } finally {
      setEnqueueingMeuTimHealthCheck(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setWhatsappFile(e.target.files[0]);
    }
  };

  const handleValidateWhatsApp = async () => {
    if (!whatsappFile) {
      showError('Selecione um arquivo Excel');
      return;
    }

    if (!whatsappEmail) {
      showError('Informe o email de destino');
      return;
    }

    try {
      setValidatingWhatsapp(true);
      const formData = new FormData();
      formData.append('excelFile', whatsappFile);
      formData.append('emailDestino', whatsappEmail);

      const result = await clientService.validateWhatsApp(formData);
      showSuccess('Validação iniciada! Você receberá o resultado por email em alguns minutos.');
      setWhatsappModalOpen(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao iniciar validação';
      showError(errorMessage);
      console.error('Erro ao validar WhatsApp:', err);
    } finally {
      setValidatingWhatsapp(false);
    }
  };

  if (!canView) {
    return (
      <GlassPageWrapper title="Gerenciamento de Clientes" icon="building">
        <Message warning>
          <Message.Header>Acesso negado</Message.Header>
          <p>Você não possui permissão para visualizar esta página.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  if (loading) {
    return (
      <GlassPageWrapper>
        <Dimmer active>
          <Loader size="large">Carregando clientes...</Loader>
        </Dimmer>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Gerenciamento de Clientes" icon="building">

      {error && (
        <Message error>
          <Message.Header>Erro</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        {isAdmin && (
          <Button 
            primary 
            onClick={handleCreateClient}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects('default')}
          >
            <Icon name="search" />
            Buscar Cliente por CNPJ
          </Button>
        )}
        {(isAdmin || user?.roles?.includes('Manager')) && (
          <Button 
            color="green"
            onClick={handleOpenWhatsappModal}
            disabled
            style={{...getDefaultButtonStyle(), marginLeft: '8px'}}
            {...getButtonHoverEffects('default')}
          >
            <Icon name="whatsapp" />
            Validar WhatsApp
          </Button>
        )}
        <Button secondary onClick={() => loadClients()}
         style={{...getDefaultButtonStyle(), marginLeft: '8px'}}
         {...getButtonHoverEffects('default')}>
          <Icon name="refresh" />
          Atualizar
        </Button>
        {isAdmin && (
          <>
            <Button
              color="blue"
              onClick={handleEnqueueMeuTimHealthCheck}
              loading={enqueueingMeuTimHealthCheck}
              disabled={selectedCount === 0}
              style={{ ...getDefaultButtonStyle(), marginLeft: '8px' }}
              {...getButtonHoverEffects('default')}
            >
              <Icon name="shield" />
              Verificar Acessos Meu TIM ({selectedCount})
            </Button>
            <Button
              onClick={clearSelectedClients}
              disabled={selectedCount === 0}
              style={{ ...getDefaultButtonStyle(), marginLeft: '8px' }}
              {...getButtonHoverEffects('default')}
            >
              <Icon name="eraser" />
              Limpar seleção
            </Button>
          </>
        )}
      </div>

      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <Form size="small">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '10px'
          }}>
            <Form.Field>
              <label>Busca geral</label>
              <Input
                placeholder="CNPJ, razão social, fantasia"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </Form.Field>
            <Form.Field>
              <label>Status cadastral</label>
              <select
                value={filters.situacaoCadastral}
                onChange={(e) => setFilters((prev) => ({ ...prev, situacaoCadastral: e.target.value }))}
                style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #ccc', padding: '0 10px' }}
              >
                <option value="">Todos</option>
                <option value="ATIVA">Ativo</option>
                <option value="INATIVA">Inativo</option>
              </select>
            </Form.Field>
            <Form.Field>
              <label>Cidade</label>
              <Input
                placeholder="Ex.: São Paulo"
                value={filters.cidade}
                onChange={(e) => setFilters((prev) => ({ ...prev, cidade: e.target.value }))}
              />
            </Form.Field>
            <Form.Field>
              <label>UF</label>
              <Input
                placeholder="Ex.: SP"
                value={filters.estado}
                onChange={(e) => setFilters((prev) => ({ ...prev, estado: e.target.value.toUpperCase() }))}
                maxLength={2}
              />
            </Form.Field>
            <Form.Field>
              <label>Classificação</label>
              <select
                value={filters.clientClassification}
                onChange={(e) => setFilters((prev) => ({ ...prev, clientClassification: e.target.value }))}
                style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #ccc', padding: '0 10px' }}
              >
                <option value="">Todas</option>
                {CLIENT_CLASSIFICATION_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Form.Field>
            <Form.Field>
              <label>Tem contrato</label>
              <select
                value={filters.temContrato}
                onChange={(e) => setFilters((prev) => ({ ...prev, temContrato: e.target.value }))}
                style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #ccc', padding: '0 10px' }}
              >
                <option value="all">Todos</option>
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </Form.Field>
            {isAdmin && (
              <Form.Field>
                <label>Status Meu TIM</label>
                <select
                  value={filters.statusMeuTim}
                  onChange={(e) => setFilters((prev) => ({ ...prev, statusMeuTim: e.target.value }))}
                  style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #ccc', padding: '0 10px' }}
                >
                  <option value="">Todos</option>
                  <option value="configurado">Tem acesso</option>
                  <option value="nao-tem-acesso">Não tem acesso</option>
                  <option value="sem-linhas">Sem linhas</option>
                </select>
              </Form.Field>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            <Button primary onClick={handleApplyFilters} style={getDefaultButtonStyle()} {...getButtonHoverEffects('default')}>
              <Icon name="filter" />
              Aplicar filtros
            </Button>
            <Button onClick={handleClearFilters}>
              <Icon name="erase" />
              Limpar filtros
            </Button>
          </div>
        </Form>
      </div>

      <div style={{ overflowX: 'auto' }}>
      <GlassTable celled>
        <Table.Header>
          <Table.Row>
            {isAdmin && (
              <Table.HeaderCell textAlign="center" collapsing>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                  title="Selecionar todos os clientes da página"
                />
              </Table.HeaderCell>
            )}
            <Table.HeaderCell textAlign="center">Resumo</Table.HeaderCell>
          <Table.HeaderCell>Ativo</Table.HeaderCell>
            <Table.HeaderCell>CNPJ</Table.HeaderCell>
            <Table.HeaderCell>Razão Social</Table.HeaderCell>
            <Table.HeaderCell>Obs. internas</Table.HeaderCell>
            <Table.HeaderCell>Nome Fantasia</Table.HeaderCell>
        
            <Table.HeaderCell>Cidade/UF</Table.HeaderCell>
            <Table.HeaderCell>Telefone</Table.HeaderCell>
            <Table.HeaderCell>Email</Table.HeaderCell>
            <Table.HeaderCell textAlign="center">Tem contratos</Table.HeaderCell>
            {isAdmin && <Table.HeaderCell textAlign="center">Meu TIM</Table.HeaderCell>}
            <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {clients.map((client) => {
            const summary = clientSummaries[client.id];
            const meuTimStatus = getMeuTimStatus(summary);
            const isSummaryLoading = loadingSummaryIds.has(client.id);
            const isExpanded = expandedClients.has(client.id);
            const clientClassification = getClientClassificationMeta(readClientClassification(client));
            const internalNotesCount = countClientInternalNoteEntries(readClientInternalNotes(client));
            const hasClientHistory = summary?.hasContract === true;

            return (
              <React.Fragment key={client.id}>
            <Table.Row>
              {isAdmin && (
                <Table.Cell textAlign="center" collapsing>
                  <input
                    type="checkbox"
                    checked={selectedClientIds.has(client.id)}
                    onChange={(e) => toggleClientSelection(client.id, e.target.checked)}
                    title="Selecionar cliente para health-check"
                  />
                </Table.Cell>
              )}
              <Table.Cell textAlign="center">
                <Button
                  basic
                  icon
                  size="mini"
                  title={isExpanded ? 'Ocultar resumo' : 'Exibir resumo'}
                  onClick={() => toggleClientExpansion(client.id)}
                  style={{ borderRadius: '999px' }}
                >
                  <Icon name={isExpanded ? 'chevron up' : 'chevron down'} />
                </Button>
              </Table.Cell>
              <Table.Cell textAlign="center">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {client.situacaoCadastral === 'ATIVA' ? (
                    <>
                      <Icon name="check circle" color="green" size="large" />
                      <span style={{ marginLeft: '8px', color: '#21ba45', fontWeight: 'bold' }}>
                        Ativo
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon name="times circle" color="red" size="large" />
                      <span style={{ marginLeft: '8px', color: '#db2828', fontWeight: 'bold' }}>
                        Inativo
                      </span>
                    </>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>{formatCNPJ(client.cnpj)}</Table.Cell>
              <Table.Cell>
                <span>{client.razaoSocial}</span>
                <Label color={clientClassification.color} size="tiny" style={{ marginLeft: 8 }}>
                  <Icon name="tag" />
                  {clientClassification.label}
                </Label>
              </Table.Cell>
              <Table.Cell>
                {canOpenClientHistory ? (
                  <button
                    type="button"
                    onClick={() => handleOpenClientHistory(client, { scrollTo: 'observacoes' })}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: internalNotesCount > 0 ? '#2185d0' : '#9ca3af',
                      textDecoration: internalNotesCount > 0 ? 'underline' : 'none',
                      font: 'inherit',
                    }}
                    title="Abrir histórico do cliente na seção de observações"
                  >
                    {internalNotesCount > 0
                      ? `${internalNotesCount} observação${internalNotesCount === 1 ? '' : 'ões'}`
                      : 'Sem observações'}
                  </button>
                ) : (
                  <span style={{ color: '#9ca3af' }}>
                    {internalNotesCount > 0
                      ? `${internalNotesCount} observação${internalNotesCount === 1 ? '' : 'ões'}`
                      : 'Sem observações'}
                  </span>
                )}
              </Table.Cell>
              <Table.Cell>{client.nomeFantasia || '-'}</Table.Cell>
              <Table.Cell>{`${client.cidade}/${client.estado}`}</Table.Cell>
              <Table.Cell>{client.telefone1 || '-'}</Table.Cell>
              <Table.Cell>{client.email || '-'}</Table.Cell>
              <Table.Cell textAlign="center">
                {isSummaryLoading ? (
                  <Loader active inline size="tiny" />
                ) : summary?.hasContract ? (
                  <Icon name="check circle" color="green" size="large" />
                ) : (
                  <Icon name="times circle" color="grey" size="large" />
                )}
              </Table.Cell>
              {isAdmin && (
                <Table.Cell textAlign="center">
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      background: `${meuTimStatus.color}20`,
                      color: meuTimStatus.color,
                      fontWeight: 700,
                      fontSize: '0.75rem'
                    }}
                  >
                    {meuTimStatus.label}
                  </span>
                </Table.Cell>
              )}
              <Table.Cell textAlign="center">
                <Button.Group size="small">
                  <Button
                    icon="eye"
                    title="Visualizar cliente"
                    onClick={() => handleViewClient(client.id)}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                  {canOpenClientHistory && (
                    <Button
                      title="Histórico de contratos"
                      onClick={() => handleOpenClientHistory(client)}
                      style={{
                        ...getDefaultButtonStyle(),
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        ...(hasClientHistory
                          ? { color: '#2185d0', borderColor: '#2185d0' }
                          : {}),
                      }}
                      {...getButtonHoverEffects('default')}
                    >
                      <Icon name="history" color={hasClientHistory ? 'blue' : undefined} />
                      {hasClientHistory ? 'Histórico' : null}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      icon={loadingClientCredentialsId === client.id ? 'spinner' : 'key'}
                      loading={loadingClientCredentialsId === client.id}
                      title="Acesso Meu TIM (credenciais no cadastro do cliente)"
                      onClick={() => handleViewMeuTimCredentials(client)}
                      style={{
                        ...getDefaultButtonStyle(),
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem'
                      }}
                      {...getButtonHoverEffects('default')}
                    />
                  )}
                  <Button
                    icon="edit"
                    title={!isAdmin ? 'Apenas administradores podem editar clientes' : 'Editar cliente'}
                    onClick={() => handleEditClient(client)}
                    disabled={!isAdmin}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                 
                  <Button
                    icon="trash"
                    title={!isAdmin ? 'Apenas administradores podem excluir clientes' : 'Excluir cliente'}
                    onClick={() => handleDeleteClient(client.id)}
                    disabled={!isAdmin}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem',
                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>

            {isExpanded && (
              <Table.Row>
                <Table.Cell colSpan={clientTableColSpan} style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                  <ClientSummaryAccordionRow
                    loading={isSummaryLoading}
                    summary={summary}
                    isAdmin={!!isAdmin}
                  />
                </Table.Cell>
              </Table.Row>
            )}
              </React.Fragment>
            );
          })}
        </Table.Body>

        {clients.length === 0 && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={clientTableColSpan} textAlign="center">
                Nenhum cliente encontrado
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}

        {clients.length > 0 && (
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan={clientTableColSpan}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    Mostrando {clients.length} de {pagination.totalCount} clientes
                    {hasClientFiltersApplied && (
                      <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>(filtros aplicados)</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Clientes por página</span>
                    <Dropdown
                      selection
                      compact
                      options={pageSizeOptions}
                      value={pagination.pageSize}
                      onChange={async (_, d) => {
                        const nextPageSize = Number(d.value);
                        setPagination((prev) => ({ ...prev, pageSize: nextPageSize }));
                        await loadClients({ pageNumber: 1, pageSize: nextPageSize });
                      }}
                    />
                    {pagination.totalPages > 1 && (
                      <Pagination
                        activePage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={(_, data) => {
                          const newPage = Number(data.activePage) || 1;
                          void loadClients({ pageNumber: newPage });
                        }}
                        size="small"
                      />
                    )}
                  </div>
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        )}
      </GlassTable>
      </div>

      <Modal 
        open={modalOpen} 
        onClose={() => {
          abortCnpjSearch();
          setModalOpen(false);
        }} 
        size="small"
        style={{
          borderRadius: '15px',
          overflow: 'hidden',
          maxWidth: '600px'
        }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #E6E6FA 0%, #B0C4DE 100%)',
          color: '#2c3e50',
          borderRadius: '15px 15px 0 0',
          padding: '1rem 1.5rem'
        }}>
          {selectedClient ? 'Editar Cliente' : 'Buscar e Criar Cliente'}
        </Modal.Header>
        <Modal.Content style={{ padding: '1rem', maxHeight: '70vh', overflowY: 'auto' }}>
          <Form size="small">
            {!selectedClient && !formFieldsVisible && (
              <div>
                <Message info compact size="small" style={{ borderRadius: '10px' }}>
                  <Message.Header>Buscar dados do CNPJ</Message.Header>
                  <p>Digite o CNPJ abaixo e clique em "Buscar" para consultar os dados automaticamente na Receita Federal.</p>
                </Message>

                <Form.Field>
                  <label>CNPJ *</label>
                  <Input
                    placeholder="00.000.000/0000-00"
                    value={formData.cnpj || ''}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    disabled={searchingCnpj}
                    size="small"
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Field>

                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <Button
                    primary
                    onClick={handleSearchCnpj}
                    loading={searchingCnpj}
                    disabled={!formData.cnpj || searchingCnpj}
                    style={getDefaultButtonStyle()}
                    {...getButtonHoverEffects('default')}
                  >
                    <Icon name="search" />
                    {searchingCnpj ? 'Buscando...' : 'Buscar Dados'}
                  </Button>
                </div>
              </div>
            )}

            {(selectedClient || formFieldsVisible) && (
              <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 0.5rem' }}>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Tipo de Cliente</label>
                    <Input
                      value={formData.tipoCliente || ''}
                      onChange={(e) => setFormData({ ...formData, tipoCliente: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Segmento</label>
                    <Input
                      value={formData.segmento || ''}
                      onChange={(e) => setFormData({ ...formData, segmento: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Classificação do Cliente</label>
                    <select
                      value={
                        normalizeClientClassificationCode(
                          typeof formData.clientClassification === 'string'
                            ? formData.clientClassification
                            : undefined
                        )
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          clientClassification: e.target.value,
                        })
                      }
                      style={{
                        width: '100%',
                        height: '38px',
                        borderRadius: '8px',
                        border: '1px solid #d4d4d5',
                        padding: '0 10px',
                        background: '#fff',
                      }}
                    >
                      {CLIENT_CLASSIFICATION_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>CNPJ</label>
                    <Input
                      value={formData.cnpj || ''}
                      onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                      disabled={selectedClient !== null} // Desabilita edição do CNPJ se for edição
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Razão Social *</label>
                    <Input
                      value={formData.razaoSocial || ''}
                      onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Field>
                  <label>Nome Fantasia</label>
                  <Input
                    value={formData.nomeFantasia || ''}
                    onChange={(e) => setFormData({ ...formData, nomeFantasia: e.target.value })}
                    size="small"
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Field>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Telefone 1</label>
                    <Input
                      value={formData.telefone1 || ''}
                      onChange={(e) => setFormData({ ...formData, telefone1: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Email</label>
                    <Input
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>CEP *</label>
                    <Input
                      value={formData.cep || ''}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Estado *</label>
                    <Input
                      value={formData.estado || ''}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Cidade *</label>
                    <Input
                      value={formData.cidade || ''}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Bairro *</label>
                    <Input
                      value={formData.bairro || ''}
                      onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Logradouro *</label>
                    <Input
                      value={formData.logradouro || ''}
                      onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Número *</label>
                    <Input
                      value={
                        formData.numero === null || formData.numero === undefined
                          ? ''
                          : String(formData.numero)
                      }
                      onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>

                <Form.Group widths="equal">
                  <Form.Field>
                    <label>Complemento</label>
                    <Input
                      value={formData.complemento || ''}
                      onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Ponto de Referência</label>
                    <Input
                      value={formData.pontoReferencia || ''}
                      onChange={(e) => setFormData({ ...formData, pontoReferencia: e.target.value })}
                      size="small"
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Field>
                </Form.Group>
              </div>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions style={{ 
          background: '#f8f9fa', 
          borderTop: '1px solid #e9ecef',
          padding: '1rem',
          borderRadius: '0 0 15px 15px'
        }}>
          <Button 
            onClick={() => {
              abortCnpjSearch();
              setModalOpen(false);
            }}
            style={{
              borderRadius: '20px',
              padding: '0.6rem 1.2rem'
            }}
          >
            Cancelar
          </Button>
          {(selectedClient || formFieldsVisible) && (
          <Button 
            primary 
            onClick={handleSaveClient}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects('default')}
          >
            <Icon name="save" />
              {selectedClient ? 'Atualizar' : 'Salvar Cliente'}
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      {/* Componente de Visualização dos Dados da Empresa */}
      <CompanyDataViewer
        data={receitaWsData ? mapClientToCompanyData(receitaWsData) : null}
        isOpen={receitaWsModalOpen}
        onClose={() => setReceitaWsModalOpen(false)}
        onConfirm={handleConfirmCnpjData}
        mode="create"
        title="Dados da Empresa - Receita Federal"
      />

      <CompanyDataViewer
        data={selectedClient ? mapClientToCompanyData(selectedClient) : null}
        isOpen={viewClientModalOpen}
        onClose={() => setViewClientModalOpen(false)}
        mode="view"
        title="Dados do Cliente"
      />

      {/* Modal de Validação de WhatsApp */}
      <Modal
        open={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        size="small"
      >
        <Modal.Header>
          <Icon name="whatsapp" color="green" />
          Validar Números de WhatsApp
        </Modal.Header>
        <Modal.Content>
          <Message info>
            <Message.Header>Como funciona?</Message.Header>
            <Message.List>
              <Message.Item>Envie um arquivo Excel (.xlsx) com uma coluna "Numero" ou "Telefone"</Message.Item>
              <Message.Item>O sistema validará cada número via Z-API (com delay de 200ms entre chamadas)</Message.Item>
              <Message.Item>Uma nova coluna "TemWhatsApp" será adicionada com "Sim" ou "Não"</Message.Item>
              <Message.Item>O arquivo processado será enviado para o email informado</Message.Item>
            </Message.List>
          </Message>

          <Form>
            <Form.Field>
              <label>Arquivo Excel (.xlsx)</label>
              <Input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
              />
              {whatsappFile && (
                <div style={{ marginTop: '8px', color: '#21ba45' }}>
                  <Icon name="check circle" />
                  {whatsappFile.name}
                </div>
              )}
            </Form.Field>

            <Form.Field>
              <label>Email de Destino</label>
              <Input
                type="email"
                placeholder="seu@email.com"
                value={whatsappEmail}
                onChange={(e) => setWhatsappEmail(e.target.value)}
                icon="mail"
              />
            </Form.Field>

            <Message warning>
              <Icon name="clock outline" />
              <strong>Importante:</strong> O processamento será feito em segundo plano. 
              Para 100 números, leva aproximadamente 20 segundos. 
              Você receberá o resultado por email quando finalizar.
            </Message>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setWhatsappModalOpen(false)}>
            <Icon name="cancel" />
            Cancelar
          </Button>
          <Button
            color="green"
            loading={validatingWhatsapp}
            disabled={!whatsappFile || !whatsappEmail}
            onClick={handleValidateWhatsApp}
          >
            <Icon name="check" />
            Iniciar Validação
          </Button>
        </Modal.Actions>
      </Modal>

      {isAdmin && (
        <CredentialsModal
          lineId={selectedLineForCredentials}
          clientIdForCredentials={credentialsClientIdForModal}
          open={credentialsModalOpen}
          onClose={() => {
            setCredentialsModalOpen(false);
            setSelectedLineForCredentials(null);
            setCredentialsClientIdForModal(null);
          }}
          onCredentialsUpdated={() => {
            loadClientSummaries(clients.map((client) => client.id));
          }}
        />
      )}

      <ClientContractHistoryModal
        open={historyModalOpen}
        clientId={historyClient?.id}
        cnpj={historyClient?.cnpj}
        razaoSocial={historyClient?.razaoSocial}
        scrollTo={historyScrollTarget}
        onClientMetadataUpdated={handleHistoryMetadataUpdated}
        onClose={() => {
          setHistoryModalOpen(false);
          setHistoryClient(null);
          setHistoryScrollTarget(undefined);
        }}
      />
    </GlassPageWrapper>
  );
};

export default ClientsPage;
